import { useEffect, useState, useCallback } from "react";
import { Loader2, CheckCircle2, ShieldAlert, Store, Ban, XCircle, PackageX, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, fmtDate, fmtDateTime, slug } from "@/lib/format";

function VoidRequestCard({ r, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null);
  const delivered = r.kind === "delivered_return";
  const decide = async (kind) => {
    const msg = delivered
      ? `Setujui void ${r.so_number}? Barang terkirim akan diretur ke stok di Odoo, invoice dibatalkan dengan nota kredit, lalu SO dibatalkan.`
      : `Setujui void ${r.so_number}? Order akan dibatalkan di Sales Hub & Odoo.`;
    if (kind === "approve" && !window.confirm(msg)) return;
    setBusy(kind);
    try {
      const res = await api.post(`/void-requests/${r.id}/${kind}`, { note: note || null });
      if (kind === "approve") {
        const rets = (res.data.data.returns || []).map((x) => x.return_picking).filter(Boolean);
        const cns = (res.data.data.credit_notes || []).flatMap((x) => x.credit_notes).filter(Boolean);
        toast.success(`${r.so_number} di-void${rets.length ? ` · retur ${rets.join(", ")}` : ""}${cns.length ? ` · nota kredit ${cns.join(", ")}` : ""}`);
      } else toast.success("Permintaan void ditolak");
      if (res.data.meta?.odoo_warning) toast.warning(res.data.meta.odoo_warning);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };
  return (
    <div className="panel approval-card void-req-card" data-testid={`void-request-${r.so_number}`}>
      <div className="approval-main">
        <div className="store-icon void"><Ban /></div>
        <div><strong>{r.outlet}</strong><span>{r.so_number}</span>
          <span className={`order-status ${slug(r.order_status || "pengiriman sebagian")} void-req-status`}>{delivered ? `${r.order_status} · perlu retur` : "Pengiriman sebagian"}</span></div>
      </div>
      <div className="void-req-body">
        <p data-testid={`void-request-reason-${r.so_number}`}><b>Alasan:</b> {r.reason}</p>
        {delivered && (
          <div className="void-req-impact" data-testid={`void-request-impact-${r.so_number}`}>
            <span><PackageX size={13} /> Diretur: {(r.delivered_lines || []).map((l) => `${l.product_name} ×${l.qty}`).join(", ") || "—"}</span>
            {(r.invoices || []).length > 0 && <span><Receipt size={13} /> Nota kredit untuk: {r.invoices.map((i) => `${i.number} (${rupiah(i.amount_total)})`).join(", ")}</span>}
          </div>
        )}
        <small>Diajukan {r.requested_by_name || r.requested_by_email} · {fmtDateTime(r.created_at)} · {rupiah(r.amount)}</small>
        <input className="rj-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan keputusan (opsional)" data-testid={`void-request-note-${r.so_number}`} />
      </div>
      <div className="approval-actions">
        <button className="rj-btn rj-btn-primary" onClick={() => decide("approve")} disabled={!!busy} data-testid={`void-approve-${r.so_number}`}>
          {busy === "approve" ? <Loader2 size={16} className="spin" /> : <><Ban size={16} /> Setujui Void</>}
        </button>
        <button className="rj-btn rj-btn-ghost" onClick={() => decide("reject")} disabled={!!busy} data-testid={`void-reject-${r.so_number}`}>
          {busy === "reject" ? <Loader2 size={16} className="spin" /> : <><XCircle size={16} /> Tolak</>}
        </button>
      </div>
    </div>
  );
}

export default function Approval() {
  const { hasPerm } = useAuth();
  const [orders, setOrders] = useState([]);
  const [voids, setVoids] = useState([]);
  const [loading, setLoading] = useState(true);
  const canApprove = hasPerm("orders:process");
  const canManage = hasPerm("approval:manage");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, v] = await Promise.all([
        canManage ? api.get("/sales", { params: { status: "Menunggu" } }) : Promise.resolve({ data: { data: [] } }),
        canManage ? api.get("/void-requests") : Promise.resolve({ data: { data: [] } }),
      ]);
      setOrders(r.data.data.filter((o) => o.credit_hold?.status !== "pending")); setVoids(v.data.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [canManage]);
  useEffect(() => { load(); }, [load]);

  const approve = async (o) => {
    try { await api.post(`/sales/${o.id}/process`); toast.success(`${o.so_number} disetujui & diproses`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="approval-page">
      <div className="section-head compact">
        <div><p className="eyebrow">PERSETUJUAN</p><h2>Approval</h2><p>Order quotation yang menunggu proses & permintaan void order yang barangnya sudah terkirim (retur stok otomatis). Persetujuan batas kredit ada di menu Finance.</p></div>
      </div>

      {loading ? <div className="rj-loading"><Loader2 className="spin" /> Memuat…</div> : (
        <>
          {canManage && (<>
          <h3 className="approval-section-title" data-testid="void-requests-title"><Ban size={16} /> Permintaan Void ({voids.length})</h3>
          {voids.length === 0 ? (
            <p className="approval-none" data-testid="void-requests-empty">Tidak ada permintaan void yang menunggu.</p>
          ) : (
            <div className="approval-list">{voids.map((r) => <VoidRequestCard key={r.id} r={r} onDone={load} />)}</div>
          )}

          <h3 className="approval-section-title"><CheckCircle2 size={16} /> Order Menunggu Proses ({orders.length})</h3>
          {!canApprove && (
            <div className="connector-alert" data-testid="approval-readonly">
              <ShieldAlert /><div><strong>Mode lihat saja</strong><span>Role Anda tidak memiliki izin memproses order. Hubungi Sales Manager / Admin.</span></div>
            </div>
          )}
          {orders.length === 0 ? (
            <div className="empty-state" data-testid="approval-empty"><CheckCircle2 /><h3>Semua order sudah diproses</h3><p>Tidak ada yang menunggu persetujuan.</p></div>
          ) : (
            <div className="approval-list">
              {orders.map((o) => (
                <div className="panel approval-card" key={o.id} data-testid={`approval-card-${o.so_number}`}>
                  <div className="approval-main">
                    <div className="store-icon"><Store /></div>
                    <div><strong>{o.outlet}</strong><span>{o.so_number} · <span className={`customer-type ${slug(o.customer_type)}`}>{o.customer_type}</span></span></div>
                  </div>
                  <div className="approval-info">
                    <div><span>Total</span><strong>{rupiah(o.amount)}</strong></div>
                    <div><span>Sales</span><strong>{o.owner_name}</strong></div>
                    <div><span>Tanggal</span><strong>{fmtDate(o.created_at)}</strong></div>
                  </div>
                  <div className="approval-actions">
                    {canApprove ? (
                      <button className="rj-btn rj-btn-green" onClick={() => approve(o)} data-testid={`approve-${o.so_number}`}><CheckCircle2 size={16} /> Setujui & Proses</button>
                    ) : (
                      <span className="rj-pill rj-pill-mock">Menunggu approver</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          </>)}
        </>
      )}
    </div>
  );
}
